import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Close, TrendingDown, YouTube } from '@mui/icons-material';
import { API_URL } from './config';
import axios from 'axios';
import { formatSubscriberCount } from './youtubeUtils';

function TerminationModal({ open, onClose, folderId, folderName }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_URL}/api/folders/${folderId}/termination-stats`);
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching termination stats:', err);
      setError(err.message || 'Failed to load termination stats');
    } finally {
      setLoading(false);
    }
  }, [folderId]);

  useEffect(() => {
    if (open && folderId) {
      fetchStats();
    } else {
      setStats(null);
      setError('');
    }
  }, [open, folderId, fetchStats]);

  const formatFullDate = (dateString) => {
    if (!dateString) {
      return 'Unknown date';
    }
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const shouldHideTerminationDate = (dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    return date.getFullYear() === 2025 && date.getMonth() === 11 && date.getDate() === 31;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background:
            'linear-gradient(145deg, rgba(30, 41, 59, 0.98) 0%, rgba(15, 23, 42, 0.98) 100%)',
          border: '1px solid rgba(148, 163, 184, 0.2)',
          borderRadius: 3,
          maxHeight: '85vh',
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TrendingDown sx={{ color: '#ef4444' }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Terminated Channels
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {folderName}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'text.secondary' }}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ minHeight: 320 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && stats && (
          <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Box
                sx={{
                  flex: 1,
                  p: 2,
                  borderRadius: 2,
                  background: 'rgba(148, 163, 184, 0.08)',
                  border: '1px solid rgba(148, 163, 184, 0.16)',
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Total Channels
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {stats.totalChannels}
                </Typography>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  p: 2,
                  borderRadius: 2,
                  background: 'rgba(239, 68, 68, 0.08)',
                  border: '1px solid rgba(239, 68, 68, 0.18)',
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Terminated
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#ef4444' }}>
                  {stats.terminatedChannels}
                </Typography>
              </Box>
            </Box>

            {stats.terminatedChannelsList?.length > 0 ? (
              <List sx={{ maxHeight: '52vh', overflow: 'auto', py: 0 }}>
                {stats.terminatedChannelsList.map((channel) => (
                  <ListItem
                    key={channel.channelId}
                    component="a"
                    href={`/channels/${channel.channelId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      py: 1.25,
                      px: 2,
                      mb: 1,
                      borderRadius: 2,
                      color: 'inherit',
                      textDecoration: 'none',
                      background: 'rgba(148, 163, 184, 0.05)',
                      '&:hover': {
                        background: 'rgba(148, 163, 184, 0.12)',
                      },
                    }}
                  >
                    <ListItemAvatar sx={{ mr: 2 }}>
                      <Avatar
                        src={channel.avatarUrl}
                        sx={{
                          width: 52,
                          height: 52,
                          background: 'linear-gradient(135deg, #ff0000 0%, #cc0000 100%)',
                        }}
                      >
                        <YouTube />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ fontWeight: 600 }}>
                            {channel.channelNameTranslation || channel.channelName}
                          </Typography>
                          <Tooltip title="Terminated">
                            <TrendingDown sx={{ fontSize: 20, color: '#ef4444' }} />
                          </Tooltip>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          {channel.subscriberCount !== undefined && (
                            <Typography variant="body2" color="text.secondary">
                              {formatSubscriberCount(channel.subscriberCount)} subscribers
                            </Typography>
                          )}
                          {channel.terminationDate &&
                            !shouldHideTerminationDate(channel.terminationDate) && (
                              <Typography variant="caption" color="error.main">
                                Terminated: {formatFullDate(channel.terminationDate)}
                              </Typography>
                            )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box sx={{ py: 8, textAlign: 'center' }}>
                <TrendingDown sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No terminated channels found
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default TerminationModal;
